import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Bell } from 'lucide-react';
import { useUserPreferences, useUpdateUserPreferences } from '@/hooks/useUserPreferences';
import { toast } from 'sonner';
import { Skeleton } from '@/components/ui/skeleton';

export function NotificationSettings() {
  const { data: prefs, isLoading } = useUserPreferences();
  const updatePrefs = useUpdateUserPreferences();

  const handleToggle = (key: string, value: boolean) => {
    updatePrefs.mutate({ [key]: value } as any, {
      onSuccess: () => toast.success('Preference updated'),
      onError: () => toast.error('Failed to update preference'),
    });
  };

  const handleThresholdChange = (value: string) => {
    const num = parseFloat(value);
    if (!isNaN(num) && num >= 0) {
      updatePrefs.mutate({ exchange_rate_threshold: num });
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-48 w-full" />
        <Skeleton className="h-48 w-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Bell className="h-5 w-5" />
            Notification Channels
          </CardTitle>
          <CardDescription>Choose how you want to receive notifications.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <Label>Email Notifications</Label>
              <p className="text-sm text-muted-foreground">Receive notifications via email.</p>
            </div>
            <Switch
              checked={prefs?.email_notifications ?? true}
              onCheckedChange={(v) => handleToggle('email_notifications', v)}
            />
          </div>
          <div className="flex items-center justify-between">
            <div>
              <Label>Push Notifications</Label>
              <p className="text-sm text-muted-foreground">Receive browser push notifications.</p>
            </div>
            <Switch
              checked={prefs?.push_notifications ?? false}
              onCheckedChange={(v) => handleToggle('push_notifications', v)}
            />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Alert Types</CardTitle>
          <CardDescription>Select which alerts you'd like to receive.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <Label>Transaction Alerts</Label>
              <p className="text-sm text-muted-foreground">Get notified about new transactions.</p>
            </div>
            <Switch
              checked={prefs?.transaction_alerts ?? true}
              onCheckedChange={(v) => handleToggle('transaction_alerts', v)}
            />
          </div>
          <div className="flex items-center justify-between">
            <div>
              <Label>Budget Alerts</Label>
              <p className="text-sm text-muted-foreground">Get notified when approaching budget limits.</p>
            </div>
            <Switch
              checked={prefs?.budget_alerts ?? true}
              onCheckedChange={(v) => handleToggle('budget_alerts', v)}
            />
          </div>
          <div className="flex items-center justify-between">
            <div>
              <Label>Exchange Rate Alerts</Label>
              <p className="text-sm text-muted-foreground">Get notified about significant rate changes.</p>
            </div>
            <Switch
              checked={prefs?.exchange_rate_alerts ?? false}
              onCheckedChange={(v) => handleToggle('exchange_rate_alerts', v)}
            />
          </div>
          {prefs?.exchange_rate_alerts && (
            <div className="space-y-2 rounded-md bg-muted p-3">
              <Label htmlFor="threshold">Alert Threshold (%)</Label>
              <p className="text-xs text-muted-foreground">Notify when rates change by this percentage.</p>
              <Input
                id="threshold"
                type="number"
                min="0"
                step="0.5"
                defaultValue={prefs?.exchange_rate_threshold ?? 5}
                onBlur={(e) => handleThresholdChange(e.target.value)}
                className="w-24"
              />
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
